// src/components/SimpleComponents/SimpleSsn.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React, { useMemo, useRef, useCallback } from 'react';
import { Form, Input } from 'antd';
import SimpleFormItem from './SimpleFormItem';
import useFormElement from 'hooks/useFormElement';
import { returnOnlyNumbers, applyNumberMask } from 'utils/helpers';
import { isValidSsn } from 'utils/validation';
import useCustomValidationStatus from 'hooks/useCustomValidationStatus';
import { useSimpleFormContext } from './SimpleFormProvider';
import translateX from 'utils/translateX';
import getValueFromFlattenObj from 'utils/getValueFromFlattenObj';
import ValidationSuffix from 'components/jarvislyComponents/ValidationSuffix.jsx';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const SSN_MASK = '999-99-9999';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleSsn = ({
  name,
  subdocumentField,
  disabled,
  onBlur,
  ...props
}) => {

  // 1. PROPS, CONTEXTS & GLOBAL STATE
  const {
    locked,
    fieldsWithError,
    disabledFields,
    data,
    form,
  } = useSimpleFormContext();

  // 2. HOOKS & REFS
  const elementProps = useFormElement(name, subdocumentField);
  const { elementRef, fullFieldName, flattenFieldName } = elementProps;

  const currentValueRaw = Form.useWatch(fullFieldName, form);
  const currentValue =
    typeof currentValueRaw === 'object' && currentValueRaw !== null
      ? currentValueRaw.value
      : currentValueRaw;

  const dataValueRaw = getValueFromFlattenObj(data, flattenFieldName);
  const dataValue =
    typeof dataValueRaw === 'object' && dataValueRaw !== null ? dataValueRaw.value : dataValueRaw;

  const {
    customValidationStatus: baseStatus,
    customValidationMessage: baseMessage,
    handleChange,
  } = useCustomValidationStatus({ ...props, ...elementProps }, isValidSsn);

  // 3. LOCAL VARIABLES I
  const isDisabled = disabled || locked || disabledFields?.includes(flattenFieldName);

  const { finalStatus, finalMessage } = useMemo(() => {
    const current = returnOnlyNumbers(currentValue);
    const saved = returnOnlyNumbers(dataValue);

    const ctxMessageKey = fieldsWithError?.[flattenFieldName];
    if (ctxMessageKey) {
      return { finalStatus: 'error', finalMessage: translateX(ctxMessageKey) };
    }

    if (!current) return { finalStatus: '', finalMessage: '' };

    if (!isValidSsn(current)) {
      return { finalStatus: 'error', finalMessage: translateX('invalid_ssn') };
    }

    if (saved && current === saved) return { finalStatus: 'success', finalMessage: '' };
    if (isValidSsn(current)) return { finalStatus: 'success', finalMessage: '' };

    return { finalStatus: baseStatus || '', finalMessage: baseMessage || '' };
  }, [currentValue, fieldsWithError, flattenFieldName, baseStatus, baseMessage, dataValue]);

  // 4. LOCAL STATE
  // 5. EFFECTS
  // 6. HANDLERS
  const handleSsnChangeWrapper = useCallback(
    ev => { handleChange(ev); },
    [handleChange]
  );

  const handleSsnBlurWrapper = ev => {
    onBlur?.(ev);
  };

  // 7. LOCAL VARIABLES II
  // 8. UI
  return (
    <SimpleFormItem
      {...props}
      name={fullFieldName}
      elementProps={elementProps}
      customValidationStatus={finalStatus}
      customValidationMessage={finalMessage}
      showFeedback={false}
    >
      <SsnInput
        ref={elementRef}
        id={flattenFieldName}
        onBlur={handleSsnBlurWrapper}
        onChange={handleSsnChangeWrapper}
        disabled={isDisabled}
        status={finalStatus}
      />
    </SimpleFormItem>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleSsn;

// *******************************************************************************
// HELPERS
// *******************************************************************************

const SsnInput = React.forwardRef(
  ({ loading, status, allowClear, prefix, suffix, onChange, onBlur, autoFocus, disabled, ...props }, ref) => {
    const stablePrefixRef = useRef(prefix ?? null);

    const staticSuffix = useMemo(
      () => <ValidationSuffix status={status || ''} loading={!!loading} extraSuffix={suffix} />,
      [status, loading, suffix]
    );

    const maskedValue = $applyMask(props.value);

    const onLocalChange = ev => {
      const digits = returnOnlyNumbers(ev?.target?.value);
      if ((digits?.length || 0) > 9) return;
      onChange && onChange(ev);
    };

    return (
      <Input
        {...props}
        ref={ref}
        value={maskedValue}
        onBlur={onBlur}
        autoFocus={autoFocus}
        prefix={stablePrefixRef.current}
        suffix={staticSuffix}
        disabled={disabled}
        onChange={onLocalChange}
        status={status || undefined}
      />
    );

    function $applyMask(val) {
      const raw = typeof val === 'object' && val !== null ? val.value : val;
      const digits = returnOnlyNumbers(raw);
      if (!digits) return '';
      return applyNumberMask(digits, SSN_MASK) || '';
    }
  }
);
